import { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { Paragraph } from '@/core/components/Typography';
import { Icon } from '@/core/components/Icons';

import { DisplaySourceMedia, extractMediaFromSources } from '@/features/Reports/helpers';

import { MediaCard } from './MediaCard';
import { MediaPreviewModal } from './MediaPreviewModal';
import {
  CountBadge,
  CountDelta,
  HeaderMeta,
  HeaderTitle,
  MediaContainer,
  MediaList,
  RefreshButton,
  RefreshStatusText,
  SectionHeader,
} from './MediaSection.styles';
import { MediaSectionProps } from './MediaSection.types';
import { useRefreshButtonSuccess } from './useRefreshButtonSuccess';
import { useRefreshStatusLabel } from './useRefreshStatusLabel';

const formatCountDelta = (delta: number): string => {
  if (delta > 0) {
    return `+${delta}`;
  }

  if (delta < 0) {
    return `${delta}`;
  }

  return '+0';
};

export const MediaSection = ({
  sources,
  refreshStatus,
  refreshUpdatedAt,
  countDelta,
  onRefresh,
}: MediaSectionProps) => {
  const { colors } = useTheme();
  const [selectedMedia, setSelectedMedia] = useState<DisplaySourceMedia | null>(null);
  const isRefreshing = refreshStatus === 'loading';
  const showButtonSuccess = useRefreshButtonSuccess(refreshStatus);
  const refreshStatusLabel = useRefreshStatusLabel(refreshStatus, refreshUpdatedAt);

  const mediaItems = useMemo(() => extractMediaFromSources(sources), [sources]);
  const formattedCountDelta = countDelta !== null ? formatCountDelta(countDelta) : null;

  return (
    <>
      <MediaContainer>
        <SectionHeader>
          <HeaderTitle>
            <CountBadge>{mediaItems.length}</CountBadge>
            {formattedCountDelta && (
              <CountDelta $delta={countDelta ?? 0}>{formattedCountDelta}</CountDelta>
            )}
            <Paragraph type="bold">Media</Paragraph>
          </HeaderTitle>
          <HeaderMeta>
            {refreshStatusLabel && (
              <RefreshStatusText $status={refreshStatus}>{refreshStatusLabel}</RefreshStatusText>
            )}
            <RefreshButton
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              $success={showButtonSuccess}
              aria-label="Update media"
            >
              <Icon
                name={showButtonSuccess ? 'Check' : 'Refresh'}
                size={14}
                color={showButtonSuccess ? colors.reliability.high : undefined}
              />
            </RefreshButton>
          </HeaderMeta>
        </SectionHeader>
        {mediaItems.length ? (
          <MediaList>
            {mediaItems.map(media => (
              <MediaCard
                key={`${media.id}-${media.sourceUrl}`}
                {...media}
                onClick={() => setSelectedMedia(media)}
              />
            ))}
          </MediaList>
        ) : (
          <Paragraph
            type="normal"
            color={colors.secondaryText}
          >
            No media available for this report.
          </Paragraph>
        )}
      </MediaContainer>
      <MediaPreviewModal
        media={selectedMedia}
        isOpen={Boolean(selectedMedia)}
        onClose={() => setSelectedMedia(null)}
      />
    </>
  );
};
